import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  useGetBitsoBanksByCountryCodeQuery,
  useGetTransactionDetailsQuery,
  useGetTransactionStatusLogsQuery,
  useLazyDownloadTransactionDetailsQuery,
} from '@/api/v1';
import { DetailsStatus } from '@/components/molecules/DetailsStatus';
import { DownloadDocumentButton } from '@/components/molecules/DownloadDocumentButton';
import { FullScreenLoader } from '@/components/molecules/FullScreenLoader';
import { TransactionDetailsDrawerContent } from '@/components/molecules/TransactionDetailsDrawerContent';
import { TransactionDetailsDrawerStatus } from '@/components/molecules/TransactionDetailsDrawerStatus';
import { TransactionDocumentsList } from '@/components/molecules/TransactionDocumentsList';
import { Accordion } from '@/components/organisms/Accordion';
import { useTranslator } from '@/hooks/useTranslator';
import { Amount } from '@/types/amount';
import {
  ACHAccountDetails,
  COPAccountDetails,
  CVUAccountDetails,
  isFiatAccount,
  PIXAccountDetails,
  SpeiAccountDetails,
  SwiftAccountDetails,
  WireAccountDetails,
} from '@/types/contacts';
import {
  isACHAccountDetails,
  isCOPAccountDetails,
  isCVUAccountDetails,
  isPIXAccountDetails,
  isSpeiAccountDetails,
  isSwiftAccountDetails,
  isWireAccountDetails,
} from '@/types/contacts.ext';
import { CurrencyCodeType } from '@/types/currency';
import { ProofOfPaymentType } from '@/types/proofOfPaymentType';
import { simplifyTransactionStatus, TransactionType } from '@/types/transaction';

enum DrawerSection {
  STATUS = 'STATUS',
  DETAILS = 'DETAILS',
  DOWNLOAD_TRANSACTION_DOCUMENTS = 'DOWNLOAD_TRANSACTION_DOCUMENTS',
  COLLAPSED = '',
  UNSET = 'UNSET',
}

export const TransactionDetailsDrawer: React.FC = (): React.ReactElement => {
  const { transactionId = '' } = useParams<{ readonly transactionId: string | undefined }>();
  const { data: transactionDetails, isLoading: isLoadingDetails } =
    useGetTransactionDetailsQuery(transactionId);
  const {
    data: { statusLogs, nextStatus } = { statusLogs: [], nextStatus: [] },
    isLoading: isLoadingStatusLogs,
  } = useGetTransactionStatusLogsQuery(transactionId);

  const [fetchProofOfPayment, fetchProofOfPaymentResult] = useLazyDownloadTransactionDetailsQuery();
  const [expandedSection, setExpandedSection] = React.useState<DrawerSection>(DrawerSection.UNSET);

  const fromCountry = React.useMemo((): string => {
    return getAccountCountry(transactionDetails?.fromAccount);
  }, [transactionDetails?.fromAccount]);

  const toCountry = React.useMemo((): string => {
    return getAccountCountry(transactionDetails?.toAccount);
  }, [transactionDetails?.toAccount]);

  const { data: fromBanks = [] } = useGetBitsoBanksByCountryCodeQuery(fromCountry, {
    skip: fromCountry === '',
  });

  const { data: toBanks = [] } = useGetBitsoBanksByCountryCodeQuery(toCountry, {
    skip: toCountry === '',
  });

  const tr = useTranslator();

  const onDownloadDocument = React.useCallback(
    (type: ProofOfPaymentType): void => {
      fetchProofOfPayment({
        transactionId: transactionId,
        type: type,
      });
    },
    [fetchProofOfPayment, transactionId],
  );

  const title = match<TransactionType | undefined, string>(transactionDetails?.type)
    .with(TransactionType.DEPOSIT, (): string =>
      tr('DEPOSIT_FROM', tr(transactionDetails?.fromAccount?.name ?? '')),
    )
    .with(TransactionType.EXCHANGE, (): string => tr('EXCHANGE'))
    .with(TransactionType.WITHDRAWAL, (): string =>
      tr('WITHDRAWAL_TO', tr(transactionDetails?.toAccount?.name ?? '')),
    )
    .with(TransactionType.TRANSFER, (): string =>
      tr('TRANSFER_TO', tr(transactionDetails?.toAccount?.name ?? '')),
    )
    .otherwise((): string => tr('TRANSACTION'));

  React.useEffect((): void => {
    if (
      fetchProofOfPaymentResult.status === QueryStatus.fulfilled ||
      fetchProofOfPaymentResult.status === QueryStatus.rejected
    ) {
      fetchProofOfPaymentResult.reset();
    }
  }, [fetchProofOfPaymentResult]);

  const onAccordionExpanded = React.useCallback((section: DrawerSection): VoidFunction => {
    return (): void => {
      setExpandedSection(
        (currentSection: DrawerSection): DrawerSection =>
          section === currentSection ? DrawerSection.COLLAPSED : section,
      );
    };
  }, []);

  React.useEffect((): void => {
    if (isLoadingStatusLogs) {
      return;
    }

    if (statusLogs.length > 0) {
      setExpandedSection(DrawerSection.STATUS);
    } else {
      setExpandedSection(DrawerSection.DETAILS);
    }
  }, [isLoadingStatusLogs, statusLogs.length]);

  const isReady = React.useMemo(
    (): boolean => expandedSection !== DrawerSection.UNSET,
    [expandedSection],
  );

  const isBRLTransaction = React.useMemo((): boolean => {
    const { fromAmount, toAmount } = transactionDetails ?? {
      fromAmount: Amount.zero(CurrencyCodeType.UNSET),
      toAmount: Amount.zero(CurrencyCodeType.UNSET),
    };

    return (
      fromAmount.currencyCode === CurrencyCodeType.BRL ||
      toAmount.currencyCode === CurrencyCodeType.BRL
    );
  }, [transactionDetails]);

  const availableDocuments = React.useMemo((): readonly ProofOfPaymentType[] => {
    return Array.from(
      new Set([
        ProofOfPaymentType.PAYMENT_CONFIRMATION,
        ...(isBRLTransaction ? [ProofOfPaymentType.COMPROVANTE_OPERACAO] : []),
      ]),
    );
  }, [isBRLTransaction]);

  if (!isReady || isLoadingDetails || isLoadingStatusLogs) {
    return (
      <Box width={612} height="100%" padding={6}>
        <FullScreenLoader loading={true} />
      </Box>
    );
  } else if (!transactionDetails) {
    return (
      <Box width={612} height="100%" padding={6}>
        <Typography variant="body1" color="error">
          {tr('TRANSACTION_NOT_FOUND')}
        </Typography>
      </Box>
    );
  } else {
    return (
      <Box width={612} height="100%" padding={6}>
        <Box sx={styles.header}>
          <Typography variant="h6">{title}</Typography>
          <DetailsStatus
            status={simplifyTransactionStatus(
              transactionDetails.status,
              transactionDetails.completed,
            )}
          />
        </Box>
        {statusLogs.length > 0 && (
          <Accordion
            baseSx={styles.accordionBase}
            detailsSx={styles.accordionDetails}
            summarySx={styles.accordionSummary}
            expanded={expandedSection === DrawerSection.STATUS}
            onChange={onAccordionExpanded(DrawerSection.STATUS)}
            label={tr('STATUS')}>
            <TransactionDetailsDrawerStatus statusLogs={statusLogs} nextStatus={nextStatus} />
          </Accordion>
        )}
        <Accordion
          baseSx={styles.accordionBase}
          detailsSx={styles.accordionDetails}
          summarySx={styles.accordionSummary}
          expanded={expandedSection === DrawerSection.DETAILS}
          onChange={onAccordionExpanded(DrawerSection.DETAILS)}
          label={tr('DETAILS')}>
          <TransactionDetailsDrawerContent
            transaction={transactionDetails}
            fromBanks={fromBanks}
            toBanks={toBanks}
          />
          {(transactionDetails?.documents ?? []).length > 0 && (
            <>
              <TransactionDocumentsList documents={transactionDetails.documents} />
            </>
          )}
        </Accordion>
        <Accordion
          baseSx={styles.accordionBase}
          detailsSx={styles.accordionDetails}
          summarySx={styles.accordionSummary}
          expanded={expandedSection === DrawerSection.DOWNLOAD_TRANSACTION_DOCUMENTS}
          onChange={onAccordionExpanded(DrawerSection.DOWNLOAD_TRANSACTION_DOCUMENTS)}
          label={tr('DOWNLOAD_TRANSACTION_DOCUMENTS')}>
          <Box mt={2}>
            {availableDocuments.map((type: ProofOfPaymentType): React.ReactElement => {
              const { type: statusType } = fetchProofOfPaymentResult.originalArgs ?? {
                type: ProofOfPaymentType.NONE,
              };

              return (
                <DownloadDocumentButton
                  key={type}
                  status={
                    statusType === type
                      ? fetchProofOfPaymentResult.status
                      : QueryStatus.uninitialized
                  }
                  disabled={fetchProofOfPaymentResult.status === QueryStatus.pending}
                  type={type}
                  onClick={onDownloadDocument}
                />
              );
            })}
          </Box>
        </Accordion>
      </Box>
    );
  }
};

const getAccountCountry = (account: unknown): string => {
  if (!isFiatAccount(account)) {
    return '';
  }

  return match(account.accountDetails)
    .when(isACHAccountDetails, (_: ACHAccountDetails): string => 'US')
    .when(isWireAccountDetails, (_: WireAccountDetails): string => 'US')
    .when(
      isSwiftAccountDetails,
      (details: SwiftAccountDetails): string => details.swiftDetails.bankInfo.country,
    )
    .when(isPIXAccountDetails, (_: PIXAccountDetails): string => 'BR')
    .when(isCOPAccountDetails, (_: COPAccountDetails): string => 'CO')
    .when(isSpeiAccountDetails, (_: SpeiAccountDetails): string => 'MX')
    .when(isCVUAccountDetails, (_: CVUAccountDetails): string => 'AR')
    .otherwise((): string => '');
};

const styles: Record<string, SystemStyleObject> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    pb: 4,
    gap: 3,
    borderBottomWidth: 0,
    borderBottomStyle: 'solid',
    borderBottomColor: 'custom.DividerMed',
  },
  accordionBase: {
    boxShadow: 'none',
    backgroundColor: 'custom.OffWhite',
    borderTop: '1px solid',
    borderTopColor: 'custom.DividerMed',
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    boxShadow: 'none',
    backgroundColor: 'custom.OffWhite',
    px: 0,
  },
  accordionDetails: {
    px: 0,
    marginTop: '-24px',
    boxShadow: 'none',
    backgroundColor: 'custom.OffWhite',
  },
  icon: {
    px: 0,
    ':hover': {
      backgroundColor: 'transparent',
    },
    ':disabled': {
      backgroundColor: 'transparent',
      color: 'custom.LightGray',
    },
  },
};
